import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle, Paperclip } from 'lucide-react'

import { cn } from '@/lib/utils'
import type { EmailThreadSummary } from '@/models/internalEmail'
import { formatAddress } from './formatAddress'

interface Props {
  thread: EmailThreadSummary
  isSelected: boolean
  onSelect: () => void
  /** Off in an org-scoped list, where it is the same name on every row. */
  showOrg?: boolean
}

export function EmailThreadListItem({ thread, isSelected, onSelect, showOrg = true }: Props) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={isSelected}
        className={cn(
          'hover:bg-muted/50 w-full cursor-pointer px-3 py-2.5 text-left',
          isSelected && 'bg-muted hover:bg-muted'
        )}
      >
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium">
            {thread.participants.map(formatAddress).join(', ') || '(no sender)'}
          </span>
          {thread.messageCount > 1 && (
            <span className="text-muted-foreground shrink-0 text-xs">{thread.messageCount}</span>
          )}
          <span className="text-muted-foreground ml-auto shrink-0 text-xs">
            {formatDistanceToNow(new Date(thread.lastDate), { addSuffix: true })}
          </span>
        </div>

        <div className="mt-0.5 flex items-center gap-1.5">
          {thread.bounced && (
            <AlertTriangle className="size-3.5 shrink-0 text-red-600" aria-label="Bounced" />
          )}
          {thread.hasAttachments && (
            <Paperclip className="text-muted-foreground size-3.5 shrink-0" aria-label="Has attachments" />
          )}
          <span className="truncate text-sm">{thread.subject || '(no subject)'}</span>
        </div>

        <div className="text-muted-foreground mt-0.5 truncate text-xs">{thread.snippet}</div>

        {showOrg && thread.org && (
          <div className="text-muted-foreground mt-1 truncate text-xs italic">{thread.org.name}</div>
        )}
      </button>
    </li>
  )
}
